import { Pipe, PipeTransform } from '@angular/core';
import * as entities from 'entities';

const DEFAULT_LIMIT = 20;
const DEFAULT_TRAIL = '...';

/**
 * Truncates a string to a specified length and appends a trail
 * by default it will truncate to 20 characters and append '...'
 * 
 * @param value - string to truncate
 * @param limit - number of characters to truncate to
 * @param trail - string to append to the end of the truncated string
 * @param type - type of string to truncate 'text' | 'html' - default is 'text'
 * 
 * using text type
 *    ex: {{ 'some string' | truncate: 10: '...' }}
 * 
 * using html type
 *    ex: <div [innerHTML]="'some html string' | truncate: 10: '...' : 'html'"></div>
 */
@Pipe({
  name: 'truncate',
})
export class TruncatePipe implements PipeTransform {

  transform(value: string, limit?: number, trail?: string, type: 'text' | 'html' = 'text'): string {

    if (!value) {
      return '';
    }

    limit = limit || DEFAULT_LIMIT;
    trail = trail || DEFAULT_TRAIL;
    
    // If type is html convert html to plain text and truncate
    if( type === 'html' ){
      return this.htmlToPlainText(value).length > limit ? this.truncateHtml(value, limit, trail) : value;
    }
    
    // If type is text truncate
    return value.length > limit ? value.substring(0, limit) + trail : value;
  }

  // Converts html to plain text
  htmlToPlainText(html: string): string{
    const code = /<.*?>|<\/.*?>/g; // strips out code
    return entities.decodeHTML(html.replace(code, ''));
  }

  // Truncates html string while preserving styles and html tags
  truncateHtml(value: string, limit?: number, trail?: string){
    
    let str = '';
    let count = 0;
    const openTag = '<';
    const closeTag = '>';
    const endTag = '/';
    let tags = [];

    // Loop through each character in the string
    for(let i = 0; i < value.length; i++){

      // If character is an open tag
      if( value[i] === openTag ){
        // Get the tag
        const tag = value.substring(i, value.substring(i, value.length).indexOf(closeTag) + i + 1);
        // Get the tag name
        // ex: <div class="some-class" id="some-id"> => div
        const tagName = tag.split(' ')[0].replace(/<|>/g, '');
        // If tag is not a self closing tag and is not an end tag add it to the stack else remove it from the stack
        if( tag.indexOf(endTag) === -1 ){
          tags.push(tagName);
        }else{
          tags.pop();
        }
        // Append tag to the string
        str+= tag;
        // Increment i by the length of the tag
        i+= tag.length - 1;
      }else{

        // Since & is already replaced with &amp; entity we can assume that anything that starts with & is an entity
        if( value[i] === '&' ){
          // Get the entity
          const entity = value.substring(i, value.substring(i, value.length).indexOf(';') + i + 1);
          // Append entity to the string
          str+= entity;
          // Increment i by the length of the entity
          i+= entity.length - 1;
        }else{
          str+= value[i];
        }
        count++;
      }

      // If count is equal to the limit append the trail to the string and break out of the loop
      if( count === limit ){
        // Append trail to the string
        str+= `<span class="truncate-dots" style="color: #000;">${trail}</span>`;

        // If tags are unclosed append tags to the end of the string
        if( tags.length > 0 ){
          for(let x = tags.length; x > 0; x--){
            str+= `${openTag}${endTag}${tags.pop()}${closeTag}`;
          }
        }

        break;
      }

    }
    return str;
  }
}
